import React, { useState } from 'react'
import { Button } from '@mui/material'
import { updatingProps, updatingSx } from '../socialdash/updatingStyle'
import UncodedDisclosureModal from './UncodedDisclosureModal'
import type { UncodedDisclosure } from '../../types/gotypes'

export interface UncodedDisclosureButtonProps {
  /** How many comments in this scope are not in the charts. */
  notAnalysed: number
  /** The breakdown the modal explains. */
  disclosure?: UncodedDisclosure
  /** The count is the previous filter's while the new one loads: drawn dimmed and busy. */
  updating?: boolean
}

/**
 * "N not analysed" — and, on click, why.
 *
 * 🔴 IT RENDERS NOTHING AT ZERO. On a scope where every comment was analysed
 * there is nothing to disclose, and a permanent "0 not analysed" would turn the
 * honest case into a standing caveat. It also renders nothing while the count
 * is still unknown, because `notAnalysed` is 0 then too — a not-yet-loaded
 * count and a real zero look identical here, and the safe reading of the
 * ambiguity is to show nothing rather than to flash a number that then changes.
 *
 * Button and modal live in ONE component, holding the open state between them,
 * so the dashboard page places a single element in the SectionHeader's action
 * slot and learns nothing about dialogs. That file's contract is arrangement
 * and copy; this is the thing being arranged.
 */
export const UncodedDisclosureButton: React.FC<UncodedDisclosureButtonProps> = ({
  notAnalysed,
  disclosure,
  updating = false,
}) => {
  const [open, setOpen] = useState(false)

  if (notAnalysed <= 0) return null

  return (
    <>
      <Button
        size="small"
        color="info"
        variant="outlined"
        onClick={() => setOpen(true)}
        data-testid="sky-uncoded-disclosure-button"
        {...updatingProps(updating)}
        sx={updatingSx(updating)}
      >
        {notAnalysed.toLocaleString()} not analysed
      </Button>
      <UncodedDisclosureModal
        open={open}
        onClose={() => setOpen(false)}
        disclosure={disclosure}
      />
    </>
  )
}

export default UncodedDisclosureButton
